import type { CandidateProfile } from "@/types/candidate-profile"
import { linkedinProfiles, type LinkedInProfile } from "./linkedin-data"

// Extended candidate type for ATS functionality
export interface ATSCandidate extends CandidateProfile {
  match_score: number
  email_status: string
  linkedin_status: string
  phone_status: string
  pipeline_stage: string
  last_contact: string | null
  recruiter_notes: string
  tags: string[]
  source: string
  added_date: string
  response_rate: number
  highlighted_matches?: string[]
  match_reasons?: string[]
  phone?: string
  email?: string
}

function convertLinkedInToATSCandidate(linkedinProfile: LinkedInProfile, index: number): ATSCandidate {
  const profile = linkedinProfile.profile
  const currentExperience = profile.experiences?.[0]
  const latestEducation = profile.education?.[0]

  // Calculate total experience duration
  const totalMonths =
    profile.experiences?.reduce((total, exp) => {
      if (!exp.starts_at?.year) return total
      const startYear = exp.starts_at.year
      const startMonth = exp.starts_at.month || 1
      const endYear = exp.ends_at?.year || new Date().getFullYear()
      const endMonth = exp.ends_at?.month || new Date().getMonth() + 1

      const months = (endYear - startYear) * 12 + (endMonth - startMonth)
      return total + Math.max(0, months)
    }, 0) || 0

  // Extract skills from headline and summary
  const skillsFromText = extractSkillsFromText(profile.headline + " " + (profile.summary || ""))

  // Generate match score based on profile completeness and experience
  const matchScore = calculateMatchScore(profile, totalMonths)

  // Convert experiences to our format
  const experiences =
    profile.experiences?.map((exp, idx) => ({
      active_experience: idx === 0 ? 1 : 0,
      position_title: exp.title,
      company_name: exp.company,
      date_from: formatDate(exp.starts_at),
      date_to: exp.ends_at ? formatDate(exp.ends_at) : undefined,
      duration_months: calculateDurationMonths(exp.starts_at, exp.ends_at),
      description: exp.description,
      location: exp.location,
      department: "Engineering", // Default since not in LinkedIn data
      management_level: inferManagementLevel(exp.title),
      company_industry: "Technology", // Default
      company_type: "Private Company", // Default
    })) || []

  // Convert education to our format
  const education =
    profile.education?.map((edu) => ({
      degree: edu.degree_name || "Unknown Degree",
      institution_name: edu.school,
      date_from_year: edu.starts_at?.year || 2020,
      date_to_year: edu.ends_at?.year || 2024,
      description: edu.field_of_study ? `Field of Study: ${edu.field_of_study}` : undefined,
    })) || []

  return {
    id: 644525819 + index, // Generate sequential IDs
    full_name: profile.full_name,
    first_name: profile.first_name,
    last_name: profile.last_name,
    headline: profile.headline,
    summary: profile.summary,
    picture_url: profile.profile_pic_url || "/placeholder.svg?height=40&width=40",
    location_full: profile.location_str,
    location_country: profile.country_full_name || profile.country || "United States",
    primary_professional_email: generateEmail(profile.first_name, profile.last_name, currentExperience?.company),
    email: generateEmail(profile.first_name, profile.last_name, currentExperience?.company),
    phone: generatePhone(),
    linkedin_url: linkedinProfile.linkedin_profile_url,
    is_working: currentExperience ? 1 : 0,
    active_experience_title: currentExperience?.title || "Software Engineer",
    active_experience_description: currentExperience?.description || "Working in software development",
    active_experience_department: "Engineering",
    active_experience_management_level: inferManagementLevel(currentExperience?.title),
    inferred_skills: skillsFromText,
    total_experience_duration_months: totalMonths,
    experience: experiences,
    education: education,
    last_graduation_date: latestEducation?.ends_at?.year || 2024,
    education_degrees: education.map((e) => e.degree),
    projected_base_salary_median: estimateSalary(currentExperience?.title, totalMonths),
    projected_total_salary_median: estimateSalary(currentExperience?.title, totalMonths) * 1.3,
    projected_base_salary_currency: "USD",
    activity: [],
    match_score: matchScore,
    email_status: "ready",
    linkedin_status: "ready",
    phone_status: "available",
    pipeline_stage: index === 0 ? "longlist" : index === 1 ? "outbound" : "interviewing",
    last_contact: index === 1 ? "2024-01-10" : null,
    recruiter_notes: index === 1 ? "Strong technical background, interested in learning more" : "",
    tags: generateTags(profile, currentExperience),
    source: "AI Search",
    added_date: "2024-01-12",
    response_rate: index === 1 ? 85 : 0,
    highlighted_matches: generateHighlightedMatches(profile, currentExperience),
    match_reasons: generateMatchReasons(profile, currentExperience, totalMonths),
  }
}

// Helper functions
function extractSkillsFromText(text: string): string[] {
  const commonSkills = [
    "JavaScript",
    "Python",
    "React",
    "Node.js",
    "TypeScript",
    "AWS",
    "Docker",
    "Kubernetes",
    "DevOps",
    "CI/CD",
    "Git",
    "SQL",
    "MongoDB",
    "PostgreSQL",
    "Redis",
    "GraphQL",
    "Machine Learning",
    "AI",
    "Data Science",
    "Spark",
    "Kafka",
    "Microservices",
    "System Design",
    "Agile",
    "Scrum",
    "Leadership",
    "Team Management",
  ]

  return commonSkills.filter((skill) => text.toLowerCase().includes(skill.toLowerCase())).slice(0, 10)
}

function calculateMatchScore(profile: any, totalMonths: number): number {
  let score = 70 // Base score

  if (profile.summary) score += 5
  if (profile.experiences?.length > 0) score += 10
  if (profile.education?.length > 0) score += 5
  if (totalMonths > 24) score += 10 // 2+ years experience
  if (totalMonths > 60) score += 5 // 5+ years experience

  return Math.min(95, score)
}

function formatDate(dateObj?: { day?: number; month?: number; year?: number }): string {
  if (!dateObj?.year) return "Unknown"
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]
  const month = dateObj.month ? months[dateObj.month - 1] : "January"
  return `${month} ${dateObj.year}`
}

function calculateDurationMonths(startDate?: any, endDate?: any): number {
  if (!startDate?.year) return 0
  const start = new Date(startDate.year, (startDate.month || 1) - 1)
  const end = endDate?.year ? new Date(endDate.year, (endDate.month || 12) - 1) : new Date()

  const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
  return Math.max(0, months)
}

function inferManagementLevel(title?: string): string {
  if (!title) return "Individual Contributor"
  const titleLower = title.toLowerCase()

  if (titleLower.includes("principal") || titleLower.includes("staff")) return "Staff"
  if (titleLower.includes("senior") || titleLower.includes("sr.")) return "Senior"
  if (titleLower.includes("lead") || titleLower.includes("manager")) return "Lead"
  if (titleLower.includes("director") || titleLower.includes("vp")) return "Director"
  if (titleLower.includes("junior") || titleLower.includes("jr.")) return "Junior"

  return "Individual Contributor"
}

function generateEmail(firstName: string, lastName: string, company?: string): string {
  const domain = company ? `${company.toLowerCase().replace(/\s+/g, "")}.com` : "example.com"
  return `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${domain}`
}

function generatePhone(): string {
  return `+1 (${Math.floor(Math.random() * 900) + 100}) 555-${String(Math.floor(Math.random() * 10000)).padStart(4, "0")}`
}

function estimateSalary(title?: string, experienceMonths?: number): number {
  const baseByLevel: Record<string, number> = {
    Junior: 70000,
    "Individual Contributor": 90000,
    Senior: 130000,
    Staff: 170000,
    Lead: 150000,
    Director: 200000,
  }

  const level = inferManagementLevel(title)
  const base = baseByLevel[level] || 90000
  const experienceMultiplier = Math.min(1.5, 1 + (experienceMonths || 0) / 120) // Cap at 1.5x for 10+ years

  return Math.round(base * experienceMultiplier)
}

function generateTags(profile: any, currentExp?: any): string[] {
  const tags = []

  if (currentExp?.title) {
    tags.push(currentExp.title)
  }
  if (currentExp?.company) {
    tags.push(currentExp.company)
  }
  if (profile.location_str?.includes("Seattle")) {
    tags.push("Seattle")
  }
  if (profile.headline?.toLowerCase().includes("senior")) {
    tags.push("Senior Level")
  }

  tags.push("AI Search")
  return tags.slice(0, 5)
}

function generateHighlightedMatches(profile: any, currentExp?: any): string[] {
  const matches = []

  if (currentExp?.title) matches.push(currentExp.title)
  if (currentExp?.company) matches.push(currentExp.company)
  if (profile.location_str) matches.push(profile.location_str.split(",")[0])

  return matches.slice(0, 4)
}

function generateMatchReasons(profile: any, currentExp?: any, totalMonths?: number): string[] {
  const reasons = []

  if (totalMonths && totalMonths > 24) {
    reasons.push(`${Math.floor(totalMonths / 12)}+ years of experience`)
  }
  if (currentExp?.title) {
    reasons.push(`Current role as ${currentExp.title}`)
  }
  if (currentExp?.company) {
    reasons.push(`Experience at ${currentExp.company}`)
  }
  if (profile.education?.length > 0) {
    reasons.push("Strong educational background")
  }

  return reasons.slice(0, 4)
}

export const mockCandidates: ATSCandidate[] = linkedinProfiles.map((profile, index) =>
  convertLinkedInToATSCandidate(profile as any, index),
)

// New pipeline stages with colors
export const PIPELINE_STAGES = {
  longlist: {
    id: "longlist",
    title: "Longlist",
    color: "border-t-slate-500",
    bgColor: "bg-slate-50",
    textColor: "text-slate-700",
  },
  shortlist: {
    id: "shortlist",
    title: "Shortlist",
    color: "border-t-blue-500",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
  },
  outbound: {
    id: "outbound",
    title: "Outbound",
    color: "border-t-purple-500",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
  },
  screening: {
    id: "screening",
    title: "Screening",
    color: "border-t-orange-500",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
  },
  submitted: {
    id: "submitted",
    title: "Submitted",
    color: "border-t-yellow-500",
    bgColor: "bg-yellow-50",
    textColor: "text-yellow-700",
  },
  interviewing: {
    id: "interviewing",
    title: "Interviewing",
    color: "border-t-teal-500",
    bgColor: "bg-teal-50",
    textColor: "text-teal-700",
  },
  rejected: {
    id: "rejected",
    title: "Rejected",
    color: "border-t-red-500",
    bgColor: "bg-red-50",
    textColor: "text-red-700",
  },
  hired: {
    id: "hired",
    title: "Hired",
    color: "border-t-green-500",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
  },
}

// Helper function to get candidate by ID
export function getCandidateById(id: number | string): ATSCandidate | undefined {
  const numericId = typeof id === "string" ? Number.parseInt(id, 10) : id
  return mockCandidates.find((candidate) => candidate.id === numericId)
}

// Helper function to get candidates by IDs
export function getCandidatesByIds(ids: (number | string)[]): ATSCandidate[] {
  const candidates = ids
    .map((id) => {
      const candidate = getCandidateById(id)
      return candidate
    })
    .filter(Boolean) as ATSCandidate[]
  return candidates
}

// Updated pipeline columns to use candidate IDs instead of full objects
export const pipelineColumns = {
  longlist: {
    id: "longlist",
    title: "Longlist",
    candidateIds: [644525819], // Only store IDs
  },
  shortlist: {
    id: "shortlist",
    title: "Shortlist",
    candidateIds: [],
  },
  outbound: {
    id: "outbound",
    title: "Outbound",
    candidateIds: [644525820],
  },
  screening: {
    id: "screening",
    title: "Screening",
    candidateIds: [],
  },
  submitted: {
    id: "submitted",
    title: "Submitted",
    candidateIds: [],
  },
  interviewing: {
    id: "interviewing",
    title: "Interviewing",
    candidateIds: [644525821],
  },
  rejected: {
    id: "rejected",
    title: "Rejected",
    candidateIds: [],
  },
  hired: {
    id: "hired",
    title: "Hired",
    candidateIds: [],
  },
}

export function addMockCandidate(candidateData: {
  name: string
  title: string
  company: string
  source: string
  linkedinUrl?: string
}): ATSCandidate {
  const newId = Math.max(...mockCandidates.map((c) => c.id)) + 1
  const [firstName, ...lastNameParts] = candidateData.name.split(" ")
  const lastName = lastNameParts.join(" ") || "Unknown"

  const newCandidate: ATSCandidate = {
    id: newId,
    full_name: candidateData.name,
    first_name: firstName,
    last_name: lastName,
    headline: candidateData.title,
    summary: `Experienced ${candidateData.title} with expertise in modern technologies and best practices.`,
    picture_url: "/placeholder.svg?height=40&width=40",
    location_full: "Toronto, Ontario, Canada",
    location_country: "Canada",
    primary_professional_email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${candidateData.company.toLowerCase().replace(/\s+/g, "")}.com`,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${candidateData.company.toLowerCase().replace(/\s+/g, "")}.com`,
    phone: `+1 (416) 555-${String(Math.floor(Math.random() * 10000)).padStart(4, "0")}`,
    linkedin_url:
      candidateData.linkedinUrl ||
      `https://www.linkedin.com/in/${candidateData.name.toLowerCase().replace(/\s+/g, "-")}`,
    is_working: 1,
    active_experience_title: candidateData.title,
    active_experience_description: `Currently working as ${candidateData.title} at ${candidateData.company}. Responsible for developing and maintaining software solutions.`,
    active_experience_department: "Engineering",
    active_experience_management_level: "Individual Contributor",
    inferred_skills: ["JavaScript", "Python", "React", "Node.js", "SQL", "Git", "Agile"],
    total_experience_duration_months: 36,
    experience: [
      {
        active_experience: 1,
        position_title: candidateData.title,
        company_name: candidateData.company,
        date_from: "January 2022",
        duration_months: 24,
        company_industry: "Technology",
        company_type: "Private Company",
        company_size_range: "100-500 employees",
        company_employees_count: 250,
        department: "Engineering",
        management_level: "Individual Contributor",
        location: "Toronto, ON, Canada",
        description: `Working as ${candidateData.title} at ${candidateData.company}. Developing software solutions and collaborating with cross-functional teams.`,
      },
    ],
    education: [
      {
        degree: "Bachelor of Computer Science",
        institution_name: "University of Toronto",
        date_from_year: 2018,
        date_to_year: 2022,
        description: "Computer Science degree with focus on software engineering and algorithms.",
      },
    ],
    last_graduation_date: 2022,
    education_degrees: ["Bachelor of Computer Science"],
    projected_base_salary_median: 85000,
    projected_total_salary_median: 95000,
    projected_base_salary_currency: "CAD",
    activity: [],
    match_score: Math.floor(Math.random() * 20) + 75, // Random score between 75-95
    email_status: "ready",
    linkedin_status: "ready",
    phone_status: "available",
    pipeline_stage: "longlist",
    last_contact: null,
    recruiter_notes: `Added from ${candidateData.source}. Profile looks promising, needs initial screening.`,
    tags: [candidateData.title, candidateData.company, candidateData.source],
    source: candidateData.source,
    added_date: new Date().toISOString().split("T")[0],
    response_rate: 0,
    highlighted_matches: [candidateData.title, candidateData.company],
    match_reasons: [
      `Experience as ${candidateData.title}`,
      `Currently working at ${candidateData.company}`,
      "Profile matches job requirements",
    ],
  }

  // Add to mock candidates array
  mockCandidates.push(newCandidate)

  return newCandidate
}
